import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

const STORAGE_KEY = "tbf_photographer_session";

export interface PhotographerSession {
  id: string;
  display_name: string;
  email: string;
  photographer_code: string;
}

interface PhotographerAuthCtx {
  session: PhotographerSession | null;
  login: (s: PhotographerSession) => void;
  logout: () => void;
  isLoading: boolean;
}

const Ctx = createContext<PhotographerAuthCtx>({
  session: null,
  login: () => {},
  logout: () => {},
  isLoading: true,
});

export function PhotographerAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PhotographerSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSession(JSON.parse(raw) as PhotographerSession);
    } catch { /* ignore */ }
    setIsLoading(false);
  }, []);

  const login = (s: PhotographerSession) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    setSession(s);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
  };

  return <Ctx.Provider value={{ session, login, logout, isLoading }}>{children}</Ctx.Provider>;
}

export function usePhotographerAuth() {
  return useContext(Ctx);
}
